// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import $ from 'jquery';
import L from 'leaflet';
import myw from 'myWorld/base/core';
import config from 'myWorld/base/config';
import './datasource';
import './datasourceCommsMixin';
import 'myWorld/base/util';
import 'myWorld/features/ddFeature';
import 'myWorld/layers/geoserverLayer';

myw.OGCDatasource = myw.Datasource.extend('OGCDatasource', {
    includes: [myw.DatasourceCommsMixin],

    defaultFeatureModel: myw.DDFeature,

    statics: {
        layerDefFields: [
            {
                name: 'wmsLayerGroup',
                type: 'string'
            },
            {
                name: 'jsClass',
                type: 'string'
            },
            {
                name: 'extraOptions',
                type: 'json',
                viewClass: 'KeyValueView',
                args: { keyTitle: 'name', valueTitle: 'value', valType: 'json' }
            },
            {
                name: 'useCacheBust',
                type: 'boolean'
            }
        ],
        specFields: [
            {
                name: 'wmsUrl',
                type: 'string',
                size: 'long',
                viewClass: 'UrlInputAndTestView',
                tableGroup: 'wms',
                args: { testMethod: 'testWms' }
            },
            {
                name: 'wmsRequestParams',
                type: 'json',
                viewClass: 'KeyValueView',
                args: { keyTitle: 'name', valueTitle: 'value' },
                tableGroup: 'wms'
            },
            { name: 'username', type: 'string', tableGroup: 'wms' },
            { name: 'password', type: 'string', tableGroup: 'wms' },

            {
                name: 'wfsUrl',
                type: 'string',
                size: 'long',
                viewClass: 'UrlInputAndTestView',
                tableGroup: 'wfs',
                args: { testMethod: 'testWfs' }
            },
            {
                name: 'wfsVersion',
                type: 'string',
                enumerator: ['1.0.0', '1.1.0', '2.0.0'],
                size: 'small',
                tableGroup: 'wfs'
            },
            {
                name: 'wfsRequestParams',
                type: 'json',
                viewClass: 'KeyValueView',
                args: { keyTitle: 'name', valueTitle: 'value' },
                tableGroup: 'wfs'
            },
            { name: 'caseInsensitive', type: 'boolean', tableGroup: 'wfs' },
            { name: 'tunnelled', type: 'boolean', tableGroup: 'wfs' }
        ]
    },

    options: {
        caseInsensitive: false,
        tunnelled: false,
        jsonpCallback: 'parseResponse',
        jsonpTimeout: 4000,

        //search related options
        fullQuery: false,
        inWindowQuery: true,

        wfsVersion: '2.0.0',

        wfsRequestParams: undefined
    },

    wfsRequestParams: {
        '1.0.0': {
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            maxFeatures: '',
            srsName: 'EPSG:4326'
        },
        '1.1.0': {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            maxFeatures: '',
            srsName: 'EPSG:4326'
        },
        '2.0.0': {
            service: 'WFS',
            version: '2.0.0',
            request: 'GetFeature',
            count: '',
            srsName: 'EPSG:4326'
        }
    },

    /**
     * @class Representation of an OGC compliant datasource
     * Provides visualization, selection and search capabilities by acessing WMS and WFS services
     * @param  {OGCOptions}    options
     * @constructs
     * @augments IDatasource
     */
    initialize(database, options) {
        myw.Datasource.prototype.initialize.call(this, database, options);

        this.requestParams = {
            outputFormat: this.options.tunnelled ? 'application/json' : 'text/javascript'
        };

        //Update the results count/ maxFeatures to match that in the config settings
        const wmsRequestParams = this.wfsRequestParams[this.options.wfsVersion];
        if (wmsRequestParams) {
            if (wmsRequestParams.hasOwnProperty('maxFeatures'))
                wmsRequestParams['maxFeatures'] = config['core.queryResultLimit'];
            else if (wmsRequestParams.hasOwnProperty('count'))
                wmsRequestParams['count'] = config['core.queryResultLimit'];
        }

        _.extend(
            this.requestParams,
            this.wfsRequestParams[this.options.wfsVersion],
            this.options.wfsRequestParams
        );
    },

    /**
     * Logs in to the datasource
     * @param  {object} credentials
     * @return {promise}
     */
    login(credentials) {
        //the wms service may require authentication so we login to wfs as well
        const requests = [this._ensureAuthenticated(`${this.options.wmsUrl}?request=GetMap`)];

        if (this.options.wfsUrl && !this.options.tunnelled) {
            //if using jsonp, login to wfs url as well, as the wms service may not require authentication,
            //in which case it won't have actually logged in.

            //future requests will use 'GetFeature' request so we should login with that request if possible
            let args;
            if (!_.isEmpty(this.featureTypes)) {
                args = `request=GetFeature&count=1&maxFeatures=1&typeName=${this.featureTypes[0]}`;
            } else {
                args = 'request=GetCapabilities';
            }
            requests.push(this._ensureAuthenticated(`${this.options.wfsUrl}?${args}`));
        }

        return Promise.all(requests).catch(error => {
            myw.trace('ogc', 1, 'Authentication error', error);
            throw error;
        });
    },

    /**
     * Instantiates a leaflet layer from a layer definition
     * @param  {layerDefinition} layerDef
     * @return {ILayer} A {@link L.TileLayer.WMS}  instance
     */
    createLayer(layerDef) {
        const layerDefFixedOptions = myw.Util.processOptionsFromJson(layerDef.extraOptions);
        const zIndex = layerDef.options && layerDef.options.zIndex;
        const options = _.extend(
            {},
            layerDef.options,
            {
                wmsLayerGroup: layerDef.wmsLayerGroup,
                featureItems: layerDef.feature_types,
                transparent: zIndex ? zIndex >= 0 : true, //ENH: this assumes basemap layers have a negative z-index. maybe add another way to specify this
                format: 'image/png'
            },
            layerDefFixedOptions
        );
        let Layer, layer;

        let url = this._getWmsUrl();

        if (layerDef.useCacheBust) {
            const separator = url.indexOf('?') > 0 ? '&' : '?';
            url += separator + Math.round(Math.random() * 1000000);
        }

        Layer = this._getLayerClassFor(layerDef, myw.GeoserverLayer);
        layer = new Layer(url, options);

        this._registerLayer(layerDef);

        return layer;
    },

    /**
     * Returns a list of image urls for displaying legends
     * @param  {object}     layerDef
     * @return {Promise<array>}      image urls
     */
    getLegendInfo(layerDef) {
        const featureTypeNames =
            layerDef.feature_types && layerDef.feature_types.length
                ? _.pluck(layerDef.feature_types, 'name')
                : [layerDef.wmsLayerGroup];

        return Promise.map(featureTypeNames, layer =>
            this.buildTunnelRequestUrl(
                {
                    request: 'GetLegendGraphic',
                    version: '1.0.0',
                    format: 'image/png',
                    layer: layer,
                    legend_options:
                        'forceLabels:on;fontColor:6d6d6d;fontSize:15;fontName:Arial;labelMargin:25;fontAntiAliasing:true;dpi:72'
                },
                {
                    urlFieldName: 'wmsUrl',
                    format: 'image/png',
                    owner: layerDef.owner
                }
            )
        );
    },

    /**
     * Creates a url for the wms service
     * @return {string}         WMS URL
     * @private
     */
    _getWmsUrl() {
        const urlParams = _.map(this.options.wmsRequestParams, (val, key) => {
            if (val) return `${key}=${val}`;
        }).join('&');
        let url = this.options.wmsUrl;
        if (url.slice(-1) == '?') url = url.slice(0, -1); //remove trailing '?'. will be added back if necessary

        if (urlParams) {
            if (url.indexOf('?') > 0) url += `&${urlParams}`;
            else url += `?${urlParams}`;
        }
        return url;
    },

    /**
     * Register that a layer is using this datasource
     * @param  {layerDefinition} layerDef
     * @private
     */
    _registerLayer(layerDef) {
        //store the definition in case it's a dynamically added layer (non stored in configuration)
        this.layerDefs[layerDef.name] = layerDef;
    },

    /**
     * Sends a request to the wfs service of underlying server
     * @param  {string} featureType
     * @param  {object} requestParams Parameters for the request
     * @return {promise<Array<Feature>>}
     */
    sendRequest(featureType, requestParams) {
        if (!this.options.wfsUrl) return Promise.resolve([]);

        const options = {
            jsonp: !this.options.tunnelled,
            jsonpCallback: this.options.jsonpCallback,
            urlFieldName: 'wfsUrl'
        };
        if (requestParams.format) options.format = requestParams.format;

        options.featureType = featureType;
        return this._ensureDDInfoFor([featureType])
            .catch(myw.MissingFeatureDD, reason => {
                //private layer or out of sync DD
                //ignore and use data to deduce DD
            })
            .return(this)
            .call('_sendFeaturesRequest', requestParams, options);
    },

    /**
     * Finds the features selectable by a user map click
     * @param  {L.LatLng}   selectionPoint  Point the user clicked/selected
     * @param  {integer}    zoomLevel       Zoom level at time of selection
     * @param  {integer}    pixelTolerance  Number of pixels to use as tolerance for the selection
     * @param  {array<Layer>}   layers  Layers relevant for selection (active and visible)
     * @return {Promise<Array<Feature>>}  Promise for the features
     */
    select(selectionPoint, zoomLevel, pixelTolerance, layers) {
        const map = layers[0].map; //Map that triggered the selection; ENH: do in caller and replace with with use of L.CRS.EPSG3857
        let featureTypeNames = [];

        _.each(layers, layer => {
            const zoomTest = (
                lfi //layerFeatureItem
            ) => lfi.min_select <= zoomLevel && zoomLevel <= lfi.max_select;
            let layerFeatureTypesNames;
            if (layer.layerDef.feature_types) {
                const featureTypes = _.filter(layer.layerDef.feature_types, zoomTest);
                layerFeatureTypesNames = _.pluck(featureTypes, 'name');
            } else {
                layerFeatureTypesNames = layer.layerDef.wmsLayerGroup;
            }
            featureTypeNames = featureTypeNames.concat(layerFeatureTypesNames);
        });
        const uniqueFeatureTypeNames = _.unique(featureTypeNames);

        return this._selectFeatures(
            uniqueFeatureTypeNames,
            selectionPoint,
            zoomLevel,
            pixelTolerance,
            map
        ).catch(reason => {
            console.log(`Datasource '${this.options.name}' selection for object failed.`, reason);
            throw reason;
        });
    },

    /**
     * Finds the features selectable by a user map box select
     * @param {bbox} selectionGeom  L.LatLngBounds that the user clicked/selected
     * @param {int} zoomLevel Zoom level at time of selection
     * @param {array<Layer>} layers Layers relevant for selection (active and visible)
     */
    async selectBox(selectionGeom, zoomLevel, layers) {
        const features = await this.select(selectionGeom, zoomLevel, null, layers);
        return features;
    },

    /**
     * Finds the features selectable by a user map click or box select
     * @param  {array}           featureTypeNames     List of FeatureType names that should be queried
     * @param  {L.LatLng}        selectionPoint       Point the user clicked/selected
     * @param  {integer}         zoomLevel            Zoom level at time of selection
     * @param  {integer}         pixelTolerance       Number of pixels to use as tolerance for the selection
     * @param  {MapControl}  map                  Map that triggered the selection
     * @return {Promise<Array<Feature>>}          Promise for the features
     * @private
     */
    _selectFeatures(featureTypeNames, selectionPoint, zoomLevel, pixelTolerance, map) {
        const bbox = selectionPoint.toBBoxString //if is already a bbox dont want to create again
            ? selectionPoint
            : map.getBoundingBoxFor(selectionPoint, zoomLevel, pixelTolerance);
        const srsName = this.requestParams.srsName;
        const srsArg = srsName ? `,${srsName}` : '';

        const sendRequestforFeature = featureTypeName => {
            const requestParams = this._getRequestParams(featureTypeName);
            requestParams.bbox = bbox.toBBoxString() + srsArg;
            return this.sendRequest(featureTypeName, requestParams);
        };

        if (this.options.tunnelled) {
            //send multiple requests and concatenate results
            const requests = featureTypeNames.map(featureTypeName =>
                sendRequestforFeature(featureTypeName)
            );
            return myw.Util.concatPromiseResults(requests);
        } else {
            //the jsonp mechanism doesn't allow us to make multiple requests simultaneously
            //serialize requests
            return Promise.reduce(
                featureTypeNames,
                (results, featureTypeName) =>
                    sendRequestforFeature(featureTypeName).then(requestResult =>
                        results.concat(requestResult)
                    ),
                []
            );
        }
    },

    /**
     * Sends an external search request
     * @param  {string}         searchTerm      Text to search for
     * @param  {searchOptions}  [options]       Options to influence the search
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     */
    runSearch(searchTerm, options) {
        const querySuggestions = this._querySuggestions(searchTerm, this.featuresDD);
        let requests;

        if (!_.isEmpty(querySuggestions)) {
            return Promise.resolve(querySuggestions);
        } else {
            const searchDetails = this._getSearchDetailsFor(searchTerm, this.featuresDD);

            //no queries matched, search for individual features
            requests = _.compact(
                _.map(searchDetails, (searchDetail, featureTypeName) => {
                    if (searchDetail.extraTerms) {
                        return this._searchFeatureTypeForTerm(
                            featureTypeName,
                            searchDetail.extraTerms
                        );
                    }
                })
            );

            return Promise.all(requests).then(results => _.flatten(results));
        }
    },

    /**
     * Sends an external search request
     * @param  {string} featureType Name of feature type
     * @param  {string} terms  Text to search for
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     * @private
     */
    _searchFeatureTypeForTerm(featureType, terms) {
        const featureDD = this.featuresDD[featureType];
        const searchProperty = featureDD && featureDD.search_fields[0];
        //ENH: support multiple fields (by doing a request per field(?))
        if (!searchProperty) return Promise.resolve([]);

        const requestParams = this._getRequestParams(featureType);

        //search for features matching the other terms (besides the identification term)
        //ENH: this is not working for wfs 2.0.0
        const matchCase = this.options.caseInsensitive ? ' matchCase="false"' : '';
        requestParams.filter = `<Filter><PropertyIsLike wildCard="*" singleChar="." escape="!"${matchCase}><PropertyName>${searchProperty}</PropertyName><Literal>${terms}*</Literal></PropertyIsLike></Filter>`;

        return this.sendRequest(featureType, requestParams).then(
            this._handleSearchResult.bind(this, featureType)
        );
    },

    /**
     * Handles external search results by returning feature Autocomplete suggestions
     * @param  {Array<Feature>} features
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     * @private
     */
    _handleSearchResult(featureType, features) {
        const featureDD = this.featuresDD[featureType],
            typeExternalName = featureDD.external_name,
            searchProperty = featureDD.searchProperty;

        return _.map(features, feature => {
            let title = feature.getTitle();
            if (title == featureType || title == typeExternalName) {
                //title is just the typeName which is not very usefull for users,
                //use the external name from the definition and the property that we searched on
                title = `${typeExternalName}: ${feature.properties[searchProperty]}`;
            }

            return {
                label: title,
                value: title,
                type: 'feature',
                data: {
                    urn: feature.getUrn()
                }
            };
        });
    },

    /**
     * Sends a query request on a given feature type
     * @param  {queryDefinition}  queryDef   As generated by results of runSearch()
     * @param  {queryOptions}     [options]
     * @return {Promise<array<MyWorldFeature>>} Promise for a list of features
     */
    runQuery(queryDef, options) {
        const params = {
            clauses: queryDef.clauses,
            includeGeoGeometry: true
        };

        if (options.bounds) params.bounds = options.bounds;
        if (options.polygon) params.geom = options.polygon;
        if (options.displayValues) params.displayValues = options.displayValues;

        return this.getFeatures(queryDef.feature_type, params);
    },

    /**
     * Obtains a feature
     * @param  {string}     featureType
     * @param  {integer}    featureId
     * @return {DDFeature}
     */
    getFeature(featureType, featureId) {
        const requestParams = this._getRequestParams(featureType);

        requestParams.featureID = featureId;

        return this.sendRequest(featureType, requestParams).then(_.first);
    },

    /**
     * Get features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply. 'geom', 'filter', 'offset', 'includeTotal' are not supported
     * @return {Promise<Array<Feature>>}    Promise to resolve with a list of the matched features
     */

    getFeatures(featureType, options) {
        let requestParams;
        try {
            requestParams = this._getRequestParams(featureType, options || {});
        } catch (err) {
            return Promise.reject(err);
        }
        return this.sendRequest(featureType, requestParams);
    },

    /**
     * Count features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply. 'geom', 'filter', 'offset', 'includeTotal' are not supported
     * @return {Promise<integer>}    Promise to resolve with a number of the matched features
     */
    countFeatures(featureType, options) {
        const requestParams = this._getRequestParams(featureType, options || {});
        requestParams.resultType = 'hits';
        requestParams.format = 'xml';
        delete requestParams.maxFeatures;
        delete requestParams.count;

        return this.sendRequest(featureType, requestParams);
    },

    /**
     * Get parameters for the request
     * @param  {string} featureType         feature type
     * @param  {Object} queryParams         query parameters. 'geom', 'filter', 'offset', 'includeTotal' are not supported
     * @return {object}                     parameters for the request
     * @private
     */
    _getRequestParams(featureType, queryParams) {
        queryParams = queryParams || {};

        const bounds = queryParams.bounds;
        let sortArray = [];
        const srsName = this.requestParams.srsName;
        let filter = '';
        const srsArg = srsName ? `,${srsName}` : '';
        const requestParams = _.extend({ typeName: featureType }, this.requestParams); //this.requestParams has the parameters depeding on the the wfs version

        if (queryParams.geom)
            throw new Error('OGC datasource: geom parameter not yet supported by getFeatures()');

        if (queryParams.clauses) {
            _.each(queryParams.clauses, clause => {
                const operatorTag = this._operatorLookup[clause.operator];
                filter += `<${operatorTag}><PropertyName>${clause.fieldName}</PropertyName><Literal>${clause.value}</Literal></${operatorTag}>`;
            });
        }

        if (filter) {
            requestParams.filter = `<ogc:Filter xmlns:ogc="http://www.opengis.net/ogc" xmlns:gml="http://www.opengis.net/gml"><And>${filter}</And></ogc:Filter>`;
        }
        if (queryParams.cqlFilter) {
            requestParams.CQL_FILTER = queryParams.cqlFilter;
        }

        if (queryParams.limit) {
            if (this.options.wfsVersion == '2.0.0') {
                requestParams.count = queryParams.limit;
            } else {
                requestParams.maxFeatures = queryParams.limit;
            }
        }

        if (bounds && !filter) {
            requestParams.bbox = bounds.toBBoxString() + srsArg;
        } else if (bounds) {
            //bbox can't be used that the same time as filter, so we need to embbed in the filter
            filter += this._getBBoxAndFilter(featureType, bounds);
        }

        if (queryParams.orderBy) {
            _.each(queryParams.orderBy, orderByDict => {
                sortArray = sortArray.concat(`${orderByDict.fieldName} ${orderByDict.order}` || '');
            });
            requestParams.sortBy = sortArray.join(',');
        }

        return requestParams;
    },

    /**
     * Returns a string to be used as part of a filter argument in a wfs getFeature request
     * @param  {string} featureType
     * @param  {L.BoundingBox} bounds
     * @return {string}
     * @private
     */
    _getBBoxAndFilter(featureType, bounds) {
        //isn't working for wfs 1.0.0. Tried Envelope tag but it wouldn't recognize it
        if (this.options.wfsVersion == '1.0.0')
            throw new Error('Setting filter and bounds not supported for  1.0.0');

        const srsName = this.requestParams.srsName;
        const srsAttr = srsName ? `srsName="${srsName}"` : '';
        const geomFieldName = this.getPrimaryGeomFieldNameFor(featureType);

        return `<ogc:BBOX><ogc:PropertyName>${geomFieldName}</ogc:PropertyName><gml:Box ${srsAttr}><gml:coordinates>${bounds.getSouth()},${bounds.getWest()} ${bounds.getNorth()},${bounds.getEast()}</gml:coordinates></gml:Box></ogc:BBOX>`;
    },

    /*
     * Query operator lookup. myWorld attribute query to MapFish rest format
     * @type {Object}
     */
    _operatorLookup: {
        '=': 'PropertyIsEqualTo',
        '<>': 'PropertyIsNotEqualTo',
        '<': 'PropertyIsLessThan',
        '<=': 'PropertyIsLessThanOrEqualTo',
        '>': 'PropertyIsGreaterThan',
        '>=': 'PropertyIsGreaterThanOrEqualTo'
    },

    /**
     * Tests the WMS URL using the options and the request params
     */
    testWms(showUI = true) {
        _.extend(this.options.wmsRequestParams, { request: 'GetCapabilities' });
        const url = this._getWmsUrl();

        const promise = this.initialized
            .then(() => {
                if (this.options.wmsUrl.length > 0)
                    return this._jsonpRequest(url, {}, this.options);
            })
            .catch(myw.JsonpParseError, error => {
                //We expect XML for this request, so a JsonpParseError is expected and means
                //the configuration is working.
                //performing a tunnelled request after a 200 response.

                const requestOptions = {
                    url: url.split('?')[0],
                    format: 'xml',
                    username: this.options.username,
                    password: this.options.password
                };

                const requestParams = { service: 'WMS', request: 'GetCapabilities' };

                let promise = this._sendRequest(requestParams, requestOptions);
                promise = promise.then(res => {
                    if (this.checkValidReturnedXml(res, true)) {
                        return res;
                    }
                });

                return promise;
            })
            .catch(reason => {
                throw reason;
            });

        return promise;
    },

    /**
     * tests the passed response if it is valid WMS or WFS xml
     * @param response
     * @param isWms
     * @returns {boolean|*}
     */
    checkValidReturnedXml(response, isWms) {
        let xml, isNodeExist;
        try {
            xml = $.parseXML(response);
        } catch (e) {
            throw new Error('Bad response format - not valid xml');
        }
        isNodeExist =
            $(xml)
                .children()
                .get(0).nodeName === (isWms ? 'WMS_Capabilities' : 'wfs:WFS_Capabilities');

        if (!isNodeExist)
            throw new Error(`Bad response format - not a ${isWms ? 'WMS' : 'WFS'} server`);

        return isNodeExist;
    },

    /**
     * Tests the WFS URL using the options and the request params
     * @param isWms bool if passed in as true, will switch to WMS tunnel test
     */
    testWfs() {
        let url = this.options.wfsUrl;
        const requestOptions = {
            jsonp: !this.options.tunnelled,
            jsonpCallback: this.options.jsonpCallback,
            url: url,
            format: 'xml',
            username: this.options.username,
            password: this.options.password
        };
        const aFeatureType = !_.isEmpty(this.featuresDD) && _.keys(this.featuresDD)[0];
        let promise = this.initialized.then(() => {
            if (!this.getName()) throw new Error('Please specify a datasource name');
        });
        let requestParams;

        if (aFeatureType) {
            requestParams = _.extend({}, this.requestParams, {
                request: 'GetFeature',
                count: '1',
                maxFeatures: '1',
                typeName: aFeatureType
            });

            promise = promise.return(
                (() => {
                    if (this.options.wfsUrl.length > 0)
                        return this._sendRequest(requestParams, requestOptions);
                })()
            );
        } else {
            //no dd, check for more common errors using the getCapabilities request
            requestParams = _.extend({}, this.requestParams, {
                request: 'GetCapabilities',
                format: 'xml'
            });

            promise = promise
                .then(() => {
                    if (this.options.wfsUrl.length > 0)
                        return this._sendRequest(requestParams, requestOptions);
                })
                .then(res => {
                    if (this.checkValidReturnedXml(res, false)) {
                        return res;
                    }
                })
                .catch(
                    myw.JsonpParseError,
                    (
                        error //We expect XML for this request, so a JsonpParseError is expected and means
                    ) =>
                        //the configuration is working
                        //ENH: if configured to use jsonp (!tunneled), we're still left not knowing if the server supports jsonp
                        //ask the user for a featureType to do the actual test
                        'success'
                )
                .catch(reason => {
                    throw reason;
                });
        }

        //figure out URL to display
        let paramsStr = '';
        _.each(requestParams, (param, name) => {
            paramsStr += `${name}=${param}&`;
        });
        url += `?${paramsStr}`;
        return promise;
    },

    /**
     * Returns the image url to test the layer def
     * @param  {layerDefinition} layerDef
     * @return {string}
     */
    getLayerURL(layerDef) {
        layerDef = _.extend({}, layerDef, layerDef.spec); //don't change original def
        let layer = this.createLayer(layerDef);

        //Adding info that is required to get the tile URL
        layer = _.extend(layer, { _crs: L.CRS.EPSG3857, _map: L.Projection.SphericalMercator });

        return decodeURI(layer.getTileUrl(L.point(2, 4)));
    },

    /**
     * Tests the datasource url for the layers config page
     * @param  {string} url        URL to test
     * @return {Promise}           When resolved returns an object with a success boolean
     */
    testLayerURL(url) {
        return this.test(url, {}).catch(error => {
            if (error.message === 'TimeoutError') {
                return { success: false };
            } else {
                //ENH: check if it is actually  myw.JsonpParseError before returning success
                return { success: true };
            }
        });
    },

    /**
     * Tests the URL for the datasource
     * @param  {string}    url           URL to test
     * @param  {booelan}   tunnelled     If the tunnelled checkbox is ticked or not
     * @param  {string}    outputFormat  Output format for the data based on the tunnelled param ('application/json' or 'text/javascript')
     * @return {Promise}                 Return either a timeout error or a invalid json error
     *                                   The timeout error means the test has failed
     */
    test(url, extraOptions, outputFormat) {
        let options = {
            jsonp: !extraOptions.tunnelled,
            jsonpCallback: this.options.jsonpCallback,
            relativeUrl: `${url}request=GetCapabilities`
        };

        options = _.extend(options, extraOptions);

        const requestParams = { outputFormat: outputFormat };

        return this._jsonpRequest(url, requestParams, options);
    },

    featureTypes() {
        return this.options.feature_types.sort();
    }
});

myw.datasourceTypes['ogc'] = myw.OGCDatasource;

/**
 * Options for {@link OGCDatasource}
 * @typedef OGCOptions
 * @property {string}    url                                Base Url to use when building requests. <br/> Usually the part of an example request up to and including the '?' character
 * @property {string}    typeName                           Type of features to select. Corresponds to the typeName parameter of WFS requests.
 * @property {boolean}   [tunnelled=false]                  Whether requests should be tunnelled via the myWorld server. If false, the service should support jsonp
 * @property {string}    [jsonpCallback='parseResponse']    Name of callback to be used with jsonp
 * @property {string}    [username]                         If provided, basic authentication credentials will be sent with requests. When using IE browsers, basic authentication requires jsonp to be disabled
 * @property {string}    [password]                         Required if username is provided
 * @property {string}    [wfsVersion=2.0.0]                 Version to use when generating wfs requests. Determines default value for 'requestParams'
 * @property {Object<string>}   [requestParams]             Key/value pairs that will be added to the request url as parameters. See 'wfsVersion'
 * @property {boolean}   [caseInsensitive=false]            When true, search requests will include the 'matchCase="false"' attribute. Some WFS servers do not support this option.
 * @property {boolean}   [fullQuery=false]                  Whether suggestion for 'full table' query should be presented to the user or not
 * @property {boolean}   [inWindowQuery=true]               Whether suggestion for 'in window' query should be presented to the user or not
 */

export default myw.OGCDatasource;
